import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Text, Heading } from '@fiverr-private/typography';
import { FiverrProLogo } from '@fiverr-private/visuals';
import { Button } from '@fiverr-private/button';
import { ElementType } from '@fiverr-private/bianka_js';
import { translate } from '../../shared/helpers';
import { sendBiankaImpression, sendBiankaClick, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { enrichUrlWithParams } from '../../shared/urlEnrichment';
import ProSectionItem from '../ProSectionItem';
import DragScrollCarousel from '../DragScrollCarousel';
import { addFadeInAnimation, addProSectionCarouselAnimation, addVanishAnimation } from '../../shared/animations';
import ProSectionPoint from './ProSectionPoint';
import { PRO_SECTION_TEXT_AND_CTA_URLS, PROS_POINTS_COUNT, PRO_SECTION_SELLERS } from './constants';

import * as styles from './styles.ve.css';

const ProSection = ({ isV16 = false }: { isV16?: boolean }) => {
  const container = useRef<HTMLDivElement>(null);
  const eventTrackingRef = useRef<HTMLDivElement>(null);
  const firstItemClass = isV16 ? '' : 'first';

  const proSectionPoints = useMemo(
    () => new Array(PROS_POINTS_COUNT).fill(null).map((_, index) => translate(`pro_section.points.${index + 1}`)),
    []
  );

  useEnteredView(eventTrackingRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.EXPERTS_CAROUSEL_IS_SHOWN,
        componentName: 'experts_carousel',
        componentText: 'ProSection',
        isV16,
      });
    },
  });

  useLayoutEffect(() => {
    (document.querySelector('.logged-out-homepage') as HTMLElement)?.style.setProperty(
      'overflow-x',
      'visible',
      'important'
    );

    const cleanupFadeInAnimation = isV16 ? undefined : addFadeInAnimation(container.current as HTMLElement);
    const cleanupVanishAnimation = addVanishAnimation(container.current as HTMLElement);
    const cleanupProSectionCarouselAnimation = addProSectionCarouselAnimation(container.current as HTMLElement, isV16);

    return () => {
      cleanupFadeInAnimation?.();
      cleanupVanishAnimation();
      cleanupProSectionCarouselAnimation();
    };
  }, [isV16]);

  const handleClick = (descriptionHeading: string, index: number) => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_EXPERTS_CAROUSEL,
      elementName: 'experts_carousel',
      elementType: ElementType.BUTTON,
      elementText: descriptionHeading,
      position: index + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_VETTED_EXPERT_CARD,
      clickedElement: descriptionHeading,
      clickedElementOrder: index + 1,
      isV16,
    });
  };

  return (
    <Container ref={container} position="relative">
      <Stack
        direction="column"
        gap={{ default: '10', sm: '12', md: '10', lg: '7', xl: '10' }}
        paddingTop={{ default: '12', sm: '16', md: '20', lg: '8', xl: '10' }}
        paddingBottom={{ default: '12', sm: '16', md: '16', lg: '8', xl: '10' }}
        paddingX={{ default: '5', sm: '8' }}
        className={styles.proSectionBlock}
        data-scroll-fade-in
        position={{ default: 'relative', lg: 'sticky' }}
      >
        <Stack direction="column" gap="6">
          <FiverrProLogo />
          <Stack direction="column" gap="4" hidden={{ default: false, lg: true }}>
            <Heading className={styles.proSectionBlockHeading}>{translate('pro_section.carousel_heading')}</Heading>
            <Text size={{ default: 'b_md', sm: 'b_lg' }}>{translate('pro_section.carousel_text')}</Text>
          </Stack>
        </Stack>
        <Stack
          direction={{ default: 'column', lg: 'row' }}
          width="100%"
          gap={{ default: '10', lg: '16' }}
          height={{ default: '342px', sm: '490px', lg: '410px', xl: '508px' }}
          className={styles.proSectionBlockCarouselContainer}
          data-scroll-carousel
        >
          <Stack
            direction="column"
            flexBasis="50%"
            gap="0"
            maxHeight="100%"
            overflowY="hidden"
            width="fit-content"
            ref={eventTrackingRef}
            hidden={{ default: true, lg: false }}
            data-description-stack
          >
            {PRO_SECTION_TEXT_AND_CTA_URLS.map((text, index) => (
              <Stack
                direction="column"
                justifyContent="center"
                flexShrink={0}
                height="100%"
                gap={{ default: '6', xl: '7' }}
                className={styles.proSectionBlockCarouselDescription}
                key={text.descriptionHeading}
                data-description-section
              >
                <Stack direction="column" justifyContent="flexEnd" flexBasis="50%">
                  <Heading
                    width={{ default: '476px', xl: index === 0 ? '585px' : '586px' }}
                    className={styles.proSectionBlockCarouselDescriptionHeading}
                  >
                    {text.descriptionHeading}
                  </Heading>
                </Stack>
                <Stack
                  direction="column"
                  gap={{ default: '6', xl: '7' }}
                  justifyContent="flexStart"
                  flexBasis="50%"
                  className={styles.proSectionBlockCarouselDescriptionTextContainer}
                >
                  <Text
                    width={{ default: '483px', xl: '493px' }}
                    size="b_md"
                    className={styles.proSectionBlockCarouselDescriptionText}
                  >
                    {text.descriptionText}
                  </Text>
                  <Container>
                    <Button
                      size="lg"
                      href={enrichUrlWithParams(text.ctaUrl, 'hplo_expert_card', index + 1)}
                      onClick={() => handleClick(text.descriptionHeading, index)}
                    >
                      {translate('pro_section.cta_text')}
                    </Button>
                  </Container>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Container
            flexBasis={{ default: '100%', lg: '50%' }}
            flexGrow={1}
            data-target-carousel
            position="relative"
            className={styles.proSectionBlockCarousel}
          >
            <Container
              width="100%"
              height="100%"
              hidden={{ default: false, lg: true }}
              className={styles.proSectionBlockCarouselSeller}
            >
              <DragScrollCarousel gap="6" paddingSpacers>
                {PRO_SECTION_SELLERS.map((seller, index) => (
                  <ProSectionItem
                    key={seller.domain}
                    src={seller.src}
                    alt={seller.alt}
                    domain={seller.domain}
                    domainDesktop={seller.domainDesktop}
                    ctaUrl={seller.ctaUrl}
                    name={seller.name}
                    avatarSrc={seller.avatarSrc}
                    position={index}
                  />
                ))}
              </DragScrollCarousel>
            </Container>
            <Container
              width="100%"
              height="100%"
              hidden={{ default: true, lg: false }}
              className={styles.proSectionBlockCarouselSeller}
              overflow="hidden"
              position="relative"
              zIndex="0"
            >
              <Stack direction="row" data-scroll-rotator className={styles.proSectionScrollRotator} width="fit-content">
                {PRO_SECTION_SELLERS.map((seller, index) => (
                  <ProSectionItem
                    key={seller.domain}
                    src={seller.src}
                    alt={seller.alt}
                    domain={seller.domain}
                    domainDesktop={seller.domainDesktop}
                    ctaUrl={seller.ctaUrl}
                    avatarSrc={seller.avatarSrc}
                    className={index === 0 ? firstItemClass : 'not-active'}
                    name={seller.name}
                    dataScrollRotatorItem={true}
                    position={index}
                    isV16={isV16}
                  />
                ))}
              </Stack>
            </Container>
          </Container>
        </Stack>
        <Stack
          direction={{ default: 'column', md: 'row' }}
          backgroundColor="grey_100"
          wrap={{ md: 'wrap', lg: 'nowrap' }}
          borderRadius="2xl"
          width="100%"
          gap={{ default: '4', sm: '6', md: '5', lg: '12' }}
          paddingY={{ default: '5', sm: '7', lg: '4', xl: '5' }}
          paddingX={{ default: '4', sm: '6', lg: '8', xl: '10' }}
          className={styles.proSectionPointContainer}
        >
          {proSectionPoints.map((text) => (
            <ProSectionPoint text={text} key={text} />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
};

export default ProSection;
